// MiniCuraAvanzado.js
// Implementación mejorada: - Auto planear en cama - Slicing aproximado (intersección triángulo/plano) - Infill simple - Estimaciones (tiempo, material, peso) - Opción: "Backend (CuraEngine)" o "Local Slicer" - Generar y descargar G-code aproximado

import React, { useRef, useEffect, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";

import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Slider,
  Divider,
  Switch,
  FormControlLabel
} from "@mui/material";

export default function MiniCuraAvanzado() {
  const mountRef = useRef(null);
  const [sceneReady, setSceneReady] = useState(false);
  const [mesh, setMesh] = useState(null);
  const [layerMeshes, setLayerMeshes] = useState([]);
  const [estimates, setEstimates] = useState({ timeSec: 0, filamentMM: 0, weightGr: 0 });

  const perfilOptions = [
    { label: "Super Quality - 0.12 mm", value: 0.12 },
    { label: "Dynamic Quality - 0.16 mm", value: 0.16 },
    { label: "Standard Quality - 0.20 mm", value: 0.20 },
    { label: "Low Quality - 0.28 mm", value: 0.28 }
  ];
  const rellenoOptions = ["0%", "10%", "20%", "30%", "50%", "100%"];
  const materialOptions = ["PLA", "PETG", "ABS"];
  const soporteOptions = ["Ninguno", "Touching Buildplate", "Everywhere"];
  const adherenciaOptions = ["Ninguna", "Borde", "Raft", "Brim"];

  const [settings, setSettings] = useState({
    perfil: 0.20,
    relleno: "20%",
    material: "PLA",
    velocidad: 60, // mm/s head speed
    soporte: "Ninguno",
    adherencia: "Borde",
    temperaturaExtrusor: 200,
    temperaturaCama: 60,
    retraccionDistancia: 6,
    retraccionVelocidad: 25,
    nozzle: 0.4,
    filamentDia: 1.75
  });

  const [transform, setTransform] = useState({ posX: 0, posY: 0, posZ: 0, scale: 1, rotX: 0, rotY: 0, rotZ: 0 });
  const [modoSegmentacion, setModoSegmentacion] = useState(false);
  const [capaActualVisible, setCapaActualVisible] = useState(0);
  const [useBackend, setUseBackend] = useState(false);
  const [gcodeText, setGcodeText] = useState(null);

  useEffect(() => {
    if (sceneReady) return;
    const width = mountRef.current.clientWidth;
    const height = mountRef.current.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5);

    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 2000);
    camera.position.set(200, 200, 200);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    mountRef.current.appendChild(renderer.domElement);

    const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 1.2);
    hemiLight.position.set(0, 200, 0);
    scene.add(hemiLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(100, 100, 100);
    scene.add(dirLight);

    const grid = new THREE.GridHelper(300, 30, "#007bff", "#999999");
    scene.add(grid);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;

    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    mountRef.current.scene = scene;
    mountRef.current.renderer = renderer;
    mountRef.current.camera = camera;

    setSceneReady(true);
    return () => mountRef.current.removeChild(renderer.domElement);
  }, []);

  // Auto-planear (project to bed) y centrar
  const autoPlaneAndCenter = (mesh) => {
    // compute bbox
    const bbox = new THREE.Box3().setFromObject(mesh);
    const min = bbox.min;
    // translate so min.z == 0
    mesh.geometry.translate(0, 0, -min.z);
    // recompute bbox and center XY
    const b2 = new THREE.Box3().setFromObject(mesh);
    const center = b2.getCenter(new THREE.Vector3());
    mesh.position.x -= center.x;
    mesh.position.y -= center.y;
  };

  const handleLoadSTL = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const loader = new STLLoader();
    const reader = new FileReader();
    reader.onload = (e) => {
      const geometry = loader.parse(e.target.result);
      geometry.computeVertexNormals();
      // ensure non-indexed so triangles are explicit
      const nonIndexed = geometry.toNonIndexed();

      const material = new THREE.MeshStandardMaterial({ color: "#007bff", metalness: 0.1, roughness: 0.6, side: THREE.DoubleSide });
      const newMesh = new THREE.Mesh(nonIndexed, material);

      // center and place on bed
      autoPlaneAndCenter(newMesh);

      if (mesh) mountRef.current.scene.remove(mesh);
      mountRef.current.scene.add(newMesh);
      setMesh(newMesh);

      clearCaps();
    };
    reader.readAsArrayBuffer(file);
  };

  useEffect(() => {
    if (mesh) {
      mesh.position.set(transform.posX, transform.posY, transform.posZ);
      mesh.scale.set(transform.scale, transform.scale, transform.scale);
      mesh.rotation.set(THREE.MathUtils.degToRad(transform.rotX), THREE.MathUtils.degToRad(transform.rotY), THREE.MathUtils.degToRad(transform.rotZ));
    }
  }, [transform, mesh]);

  // Slicing local: triangle-plane intersection method
  function sliceMeshToLayers(mesh, layerHeight) {
    const geo = mesh.geometry; // non-indexed BufferGeometry
    const pos = geo.getAttribute("position");
    const triCount = pos.count / 3;

    const bbox = new THREE.Box3().setFromObject(mesh);
    const minZ = bbox.min.z;
    const maxZ = bbox.max.z;
    const totalAlt = maxZ - minZ;
    const numLayers = Math.ceil(totalAlt / layerHeight);

    const layers = [];

    for (let li = 0; li < numLayers; li++) {
      const z = minZ + li * layerHeight + 1e-6; // small offset
      const segments = [];

      for (let t = 0; t < triCount; t++) {
        const ax = pos.getX(t * 3), ay = pos.getY(t * 3), az = pos.getZ(t * 3);
        const bx = pos.getX(t * 3 + 1), by = pos.getY(t * 3 + 1), bz = pos.getZ(t * 3 + 1);
        const cx = pos.getX(t * 3 + 2), cy = pos.getY(t * 3 + 2), cz = pos.getZ(t * 3 + 2);

        // check intersection of triangle edges with plane z = value
        const verts = [ {x:ax,y:ay,z:az}, {x:bx,y:by,z:bz}, {x:cx,y:cy,z:cz} ];
        const intersectPts = [];

        for (let e = 0; e < 3; e++) {
          const v1 = verts[e];
          const v2 = verts[(e+1)%3];
          const z1 = v1.z, z2 = v2.z;
          if ((z1 <= z && z2 >= z) || (z1 >= z && z2 <= z)) {
            if (Math.abs(z2 - z1) < 1e-9) continue; // skip degenerate
            const tpar = (z - z1) / (z2 - z1);
            if (tpar >= 0 && tpar <= 1) {
              const x = v1.x + (v2.x - v1.x) * tpar;
              const y = v1.y + (v2.y - v1.y) * tpar;
              intersectPts.push([x,y]);
            }
          }
        }

        if (intersectPts.length === 2) {
          segments.push({ a: intersectPts[0], b: intersectPts[1] });
        }
      }

      // Build a geometry with all segments
      const positions = [];
      segments.forEach(s => {
        positions.push(s.a[0], s.a[1], z);
        positions.push(s.b[0], s.b[1], z);
      });

      const geom = new THREE.BufferGeometry();
      if (positions.length >= 6) geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      layers.push({ z, segments, geom });
    }

    return { layers, minZ, maxZ };
  }

  const hacerSegmentacion = async () => {
    if (!mesh) return;
    clearCaps();

    if (useBackend) {
      // enviar STL al backend - el backend esperado debe ejecutar CuraEngine
      // el endpoint es /api/slice (debes implementarlo en tu servidor) que retorna { gcode: string }
      const stlBlob = new Blob([mesh.geometry.attributes.position.array.buffer], { type: 'application/octet-stream' });
      // Warning: this is a placeholder. Mejor enviar el archivo original desde input.
      alert('Modo backend activado: se espera un endpoint /api/slice. En esta demo no se envía el archivo. Implementa backend para usar CuraEngine.');
      return;
    }

    const layerHeight = settings.perfil;
    const { layers, minZ, maxZ } = sliceMeshToLayers(mesh, layerHeight);

    // crear meshes visibles por capa (líneas) y añadir a escena
    const created = [];
    layers.forEach((layer, idx) => {
      if (!layer.geom || !layer.geom.attributes.position) {
        created.push(null);
        return;
      }
      const mat = new THREE.LineBasicMaterial({ linewidth: 1, color: idx % 2 === 0 ? 0xff5500 : 0x00aa00 });
      const lines = new THREE.LineSegments(layer.geom, mat);
      lines.visible = idx === 0;
      mountRef.current.scene.add(lines);
      created.push(lines);
    });

    // generar infill simple para cada capa: rayas dentro bounding box
    const bbox = new THREE.Box3().setFromObject(mesh);
    const sizeX = bbox.max.x - bbox.min.x;
    const sizeY = bbox.max.y - bbox.min.y;
    const infillPercent = parseInt(settings.relleno.replace('%',''));
    const spacing = Math.max(0.8, 5 * (100 / Math.max(1, infillPercent))); // heurística

    const infillMeshes = layers.map((layer, idx) => {
      const positions = [];
      for (let x = bbox.min.x; x <= bbox.max.x; x += spacing) {
        positions.push(x, bbox.min.y, layer.z);
        positions.push(x, bbox.max.y, layer.z);
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      const m = new THREE.LineSegments(g, new THREE.LineBasicMaterial({ color: 0x0066ff }));
      m.visible = (idx === 0);
      mountRef.current.scene.add(m);
      created[idx] && created[idx].add(m);
      return m;
    });

    setLayerMeshes(created.filter(Boolean));
    setModoSegmentacion(true);
    setCapaActualVisible(0);

    // estimaciones: longitud total aproximada
    let totalLen = 0;
    layers.forEach((layer) => {
      layer.segments.forEach(s => {
        const dx = s.a[0]-s.b[0];
        const dy = s.a[1]-s.b[1];
        totalLen += Math.sqrt(dx*dx + dy*dy);
      });
      // infill approx
      totalLen += (sizeY / spacing) * sizeX * 0.5; // heurística
    });

    // convert to extrusion length mm of filament (approx). We'll assume single line = nozzle width * layerHeight area
    const nozzleArea = settings.nozzle * settings.perfil; // mm^2 approx per mm of path (this is simplified)
    // filament cross-section area
    const filamentArea = Math.PI * Math.pow(settings.filamentDia/2, 2);
    const filamentMM = (totalLen * nozzleArea) / filamentArea; // mm of filament
    const density = settings.material === 'PLA' ? 1.24 : settings.material === 'PETG' ? 1.27 : 1.04; // g/cm3
    const weightGr = (filamentMM / 1000) * (filamentArea / 100) * density * 1000; // approx grams

    const timeSec = totalLen / settings.velocidad; // seconds (very rough)

    setEstimates({ timeSec, filamentMM: Math.round(filamentMM), weightGr: Math.round(weightGr) });

    // guardar también gcode aproximado en memoria
    const gcode = generateSimpleGcode(layers, bbox, settings);
    setGcodeText(gcode);
  };

  const handleCapaVisible = (i) => {
    if (!modoSegmentacion) return;
    setCapaActualVisible(i);
    // toggle visibility
    const visibleIdx = i;
    let li = 0;
    // layerMeshes correspond only to layers that had geometry; assume order
    layerMeshes.forEach((m, idx) => {
      m.visible = idx === visibleIdx;
    });
  };

  const clearCaps = () => {
    layerMeshes.forEach((m) => {
      try { mountRef.current.scene.remove(m); } catch (e) {}
    });
    setLayerMeshes([]);
    setModoSegmentacion(false);
    setCapaActualVisible(0);
    setEstimates({ timeSec: 0, filamentMM: 0, weightGr: 0 });
    setGcodeText(null);
  };

  useEffect(() => {
    if (modoSegmentacion) hacerSegmentacion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.perfil]);

  // Generador simple de G-code (muy aproximado)
  function generateSimpleGcode(layers, bbox, settings){
    let lines = [];
    lines.push('; Generated by MiniCuraAvanzado (approximate G-code)');
    lines.push('G21 ; mm');
    lines.push('G90 ; absolute');
    lines.push(`M104 S${settings.temperaturaExtrusor}`);
    lines.push(`M140 S${settings.temperaturaCama}`);
    lines.push('G28 ; home');
    lines.push('G92 E0');

    let e = 0; // extrusion in mm of filament approx
    const filamentArea = Math.PI * Math.pow(settings.filamentDia/2, 2);
    const nozzleArea = settings.nozzle * settings.perfil;
    const eFactor = nozzleArea / filamentArea;

    layers.forEach((layer, li) => {
      lines.push(`; Layer ${li} Z=${layer.z.toFixed(3)}`);
      lines.push(`G1 Z${layer.z.toFixed(3)} F1200`);

      // perimeters: use segments
      layer.segments.forEach((s) => {
        const ax = s.a[0].toFixed(3), ay = s.a[1].toFixed(3);
        const bx = s.b[0].toFixed(3), by = s.b[1].toFixed(3);
        lines.push(`G0 X${ax} Y${ay} F3000`);
        const dist = Math.hypot(s.a[0]-s.b[0], s.a[1]-s.b[1]);
        const de = dist * eFactor;
        e += de;
        lines.push(`G1 X${bx} Y${by} E${e.toFixed(5)} F${settings.velocidad*60}`);
      });

      // infill simplistic: vertical lines inside bbox
      const spacing = Math.max(0.8, 5 * (100 / Math.max(1, parseInt(settings.relleno.replace('%','')))));
      for (let x = bbox.min.x; x <= bbox.max.x; x += spacing){
        lines.push(`G0 X${x.toFixed(3)} Y${bbox.min.y.toFixed(3)} F3000`);
        const dist = Math.abs(bbox.max.y - bbox.min.y);
        const de = dist * eFactor;
        e += de;
        lines.push(`G1 X${x.toFixed(3)} Y${bbox.max.y.toFixed(3)} E${e.toFixed(5)} F${settings.velocidad*60}`);
      }
    });

    lines.push('M104 S0');
    lines.push('M140 S0');
    lines.push('G28 X0');
    lines.push('M84');
    return lines.join('\n');
  }

  const downloadGcode = () => {
    if (!gcodeText) return alert('No hay G-code generado. Activa segmentación para generar.');
    const blob = new Blob([gcodeText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'mini_cura_approx.gcode';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Paper elevation={4} sx={{ width: 360, p:2, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap:2 }}>
        <Typography variant="h6">Mini Cura Avanzado (Demo)</Typography>

        <FormControlLabel control={<Switch checked={useBackend} onChange={(e)=>setUseBackend(e.target.checked)} />} label={useBackend ? 'Modo: Backend (CuraEngine)' : 'Modo: Local Slicer (JS)'} />

        <TextField select label="Perfil / Altura de capa" value={settings.perfil} onChange={(e)=>setSettings({...settings, perfil: parseFloat(e.target.value)})}>
          {perfilOptions.map((opt)=> <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
        </TextField>

        <TextField select label="Relleno" value={settings.relleno} onChange={(e)=>setSettings({...settings, relleno: e.target.value})}>
          {rellenoOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
        </TextField>

        <TextField select label="Material" value={settings.material} onChange={(e)=>setSettings({...settings, material: e.target.value})}>
          {materialOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
        </TextField>

        <TextField label="Velocidad (mm/s)" type="number" value={settings.velocidad} onChange={(e)=>setSettings({...settings, velocidad: parseFloat(e.target.value)})} />

        <TextField select label="Soporte" value={settings.soporte} onChange={(e)=>setSettings({...settings, soporte: e.target.value})}>
          {soporteOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
        </TextField>

        <TextField select label="Adherencia" value={settings.adherencia} onChange={(e)=>setSettings({...settings, adherencia: e.target.value})}>
          {adherenciaOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
        </TextField>

        <Divider />
        <Typography variant="h6">Retracción / Temperaturas</Typography>
        <TextField label="Temperatura extrusor (°C)" type="number" value={settings.temperaturaExtrusor} onChange={(e)=>setSettings({...settings, temperaturaExtrusor: parseFloat(e.target.value)})} />
        <TextField label="Temperatura cama (°C)" type="number" value={settings.temperaturaCama} onChange={(e)=>setSettings({...settings, temperaturaCama: parseFloat(e.target.value)})} />
        <TextField label="Retracción (mm)" type="number" value={settings.retraccionDistancia} onChange={(e)=>setSettings({...settings, retraccionDistancia: parseFloat(e.target.value)})} />
        <TextField label="Velocidad retracción (mm/s)" type="number" value={settings.retraccionVelocidad} onChange={(e)=>setSettings({...settings, retraccionVelocidad: parseFloat(e.target.value)})} />

        <Divider />
        <Typography variant="h6">Transformaciones</Typography>
        {['posX','posY','posZ','scale','rotX','rotY','rotZ'].map(field => (
          <Box key={field}>
            <Typography variant="body2">{field}</Typography>
            <Slider min={field==='scale'?0.1:-200} max={field==='scale'?5:200} step={field==='scale'?0.01:1} value={transform[field]} onChange={(e,val)=>setTransform({...transform,[field]:val})} valueLabelDisplay="auto" />
          </Box>
        ))}

        <Button variant="outlined" color="secondary" onClick={()=>setTransform({posX:0,posY:0,posZ:0,scale:1,rotX:0,rotY:0,rotZ:0})}>Resetear Transformaciones</Button>

        <Button variant="contained" component="label">Cargar STL<input type="file" hidden accept=".stl" onChange={handleLoadSTL} /></Button>

        <Button variant="contained" color={modoSegmentacion?"secondary":"primary"} onClick={()=>{ if(modoSegmentacion) clearCaps(); else hacerSegmentacion(); }}>{modoSegmentacion? 'Desactivar Segmentación' : 'Activar Segmentación'}</Button>

        {modoSegmentacion && (
          <>
            <Typography variant="body2">Capa visible: {capaActualVisible + 1}</Typography>
            <Slider min={0} max={layerMeshes.length>0? layerMeshes.length-1: 0} step={1} value={capaActualVisible} onChange={(e,val)=>handleCapaVisible(val)} valueLabelDisplay="auto" />

            <Typography variant="body2">Estimaciones:</Typography>
            <Typography variant="caption">Tiempo aproximado: {(estimates.timeSec/60).toFixed(1)} min</Typography>
            <Typography variant="caption">Filamento (mm): {estimates.filamentMM}</Typography>
            <Typography variant="caption">Peso estimado (g): {estimates.weightGr}</Typography>

            <Button variant="outlined" onClick={downloadGcode} sx={{ mt:1 }}>Descargar G-code Aproximado</Button>
          </>
        )}

      </Paper>

      <Box ref={mountRef} sx={{ flex:1, border:'2px solid #007bff', borderRadius:2, position:'relative' }} />
    </Box>
  );
}
